import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  Zap,
  Plus,
  Search,
  Filter,
  FolderPlus,
  Sparkles,
  MoreVertical,
  Play,
  Pause,
  Copy,
  Trash2,
  Edit,
  Users,
  Clock,
  ChevronRight,
  FileText,
  Settings,
  BarChart3,
  Folder,
  Star,
  Archive,
  AlertCircle,
  CheckCircle2,
  Eye,
  Loader2,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';
import WorkflowTemplateLibrary from '../../../components/automations/WorkflowTemplateLibrary';
import CreateWorkflowMenu from '../../../components/automations/CreateWorkflowMenu';
import WorkflowActionsMenu from '../../../components/automations/WorkflowActionsMenu';
import WorkflowImportDialog from '../../../components/automations/WorkflowImportDialog';
import DeleteWorkflowModal from '../../../components/automations/DeleteWorkflowModal';
import MoveWorkflowFolderModal from '../../../components/automations/MoveWorkflowFolderModal';
import {
  deleteAgencyAutomation,
  duplicateAgencyAutomation,
  getAgencyBuilderPath,
  toggleAgencyAutomation,
} from '../../../services/workflowListService';
import {
  downloadAgencyWorkflow,
  getSafeWorkflowPortableError,
  triggerBrowserDownload,
} from '../../../services/workflowPortableService';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Trigger category folders
const TRIGGER_FOLDERS = [
  { id: 'all', name: 'All Workflows', icon: Zap },
  { id: 'postgrid', name: 'PostGrid / Mail', icon: Folder },
  { id: 'email', name: 'Email', icon: Folder },
  { id: 'dispute', name: 'Disputes', icon: Folder },
  { id: 'crm', name: 'CRM / Contacts', icon: Folder },
  { id: 'usage', name: 'Usage / Billing', icon: Folder },
];

const SMART_LISTS = [
  { id: 'needs-review', name: 'Needs Review', count: 0, icon: AlertCircle },
  { id: 'favorites', name: 'Favorites', count: 0, icon: Star },
  { id: 'archived', name: 'Archived', count: 0, icon: Archive },
];

const StatusBadge = ({ status }) => {
  const config = {
    active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    paused: { label: 'Paused', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    draft: { label: 'Draft', className: 'bg-slate-100 text-slate-600 border-slate-200' },
  };
  const { label, className } = config[status] || config.draft;
  return <Badge variant="outline" className={cn('text-xs', className)}>{label}</Badge>;
};

const AutomationsPage = () => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [showCreateMenu, setShowCreateMenu] = useState(false);
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false);
  const [deleteWorkflowTarget, setDeleteWorkflowTarget] = useState(null);
  const [moveFolderWorkflow, setMoveFolderWorkflow] = useState(null);
  const [deletingWorkflow, setDeletingWorkflow] = useState(false);
  const [workflowImportOpen, setWorkflowImportOpen] = useState(false);
  const [workflowImportMode, setWorkflowImportMode] = useState('file');
  const [downloadingWorkflowId, setDownloadingWorkflowId] = useState(null);

  const activeTab = searchParams.get('tab') || 'workflows';

  const openFileImport = () => {
    setWorkflowImportMode('file');
    setWorkflowImportOpen(true);
  };

  const openUrlImport = () => {
    setWorkflowImportMode('url');
    setWorkflowImportOpen(true);
  };

  // Fetch automations from real API
  const fetchAutomations = useCallback(async () => {
    if (!token || !agencyId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedFolder !== 'all') {
        params.set('trigger_category', selectedFolder);
      }
      if (searchQuery) {
        params.set('search', searchQuery);
      }
      
      const res = await axios.get(
        `${API}/automations/agency/${agencyId}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWorkflows(res.data.automations || []);
    } catch (err) {
      console.error('Failed to fetch automations:', err);
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, [token, agencyId, selectedFolder, searchQuery]);

  const handleWorkflowImported = (workflow) => {
    setWorkflowImportOpen(false);
    toast.success('Workflow imported as draft');
    fetchAutomations();
    const workflowId = workflow?.id || workflow?.workflow_id;
    if (workflowId) {
      navigate(getAgencyBuilderPath(agencyId, workflowId));
    }
  };

  const handleDownloadWorkflow = async (workflow) => {
    const id = workflow?.id || workflow?.workflow_id;
    if (!id || !agencyId || downloadingWorkflowId) return;
    setDownloadingWorkflowId(id);
    try {
      const { blob, filename } = await downloadAgencyWorkflow(
        token,
        agencyId,
        id,
        workflow?.name || 'workflow',
      );
      triggerBrowserDownload(blob, filename);
      toast.success('Workflow downloaded');
    } catch (err) {
      toast.error(getSafeWorkflowPortableError(err, 'Unable to download this workflow.'));
    } finally {
      setDownloadingWorkflowId(null);
    }
  };

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(
        `${API}/automations/templates`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
    }
  }, [token]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    if (!token || !agencyId) return;
    try {
      const res = await axios.get(
        `${API}/automations/stats/agency/${agencyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [token, agencyId]);

  useEffect(() => {
    fetchAutomations();
    fetchTemplates();
    fetchStats();
  }, [fetchAutomations, fetchTemplates, fetchStats]);

  const handleTabChange = (tab) => {
    setSearchParams({ tab });
  };

  const handleCreateWorkflow = async (option) => {
    setShowCreateMenu(false);
    if (option === 'template') {
      setShowTemplateLibrary(true);
    } else if (option === 'scratch') {
      navigate(`/agency/${agencyId}/automations/builder/new`);
    } else if (option === 'ai') {
      navigate(`/agency/${agencyId}/automations/builder/new?mode=ai`);
    }
  };

  const handleSelectTemplate = async (template) => {
    setShowTemplateLibrary(false);
    try {
      const res = await axios.post(
        `${API}/automations/from-template/${template.id}?agency_id=${agencyId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Automation created from template');
      fetchAutomations();
      navigate(`/agency/${agencyId}/automations/builder/${res.data.id}`);
    } catch (err) {
      toast.error('Failed to create automation from template');
    }
  };

  const handleWorkflowAction = async (workflowId, action) => {
    if (action === 'edit') {
      navigate(getAgencyBuilderPath(agencyId, workflowId));
    } else if (action === 'send-test') {
      navigate(getAgencyBuilderPath(agencyId, workflowId, '?panel=test'));
    } else if (action === 'duplicate') {
      try {
        await duplicateAgencyAutomation(token, workflowId, agencyId);
        toast.success('Automation duplicated');
        fetchAutomations();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to duplicate automation');
      }
    } else if (action === 'pause') {
      try {
        await toggleAgencyAutomation(token, agencyId, workflowId);
        toast.success('Automation paused');
        fetchAutomations();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to pause automation');
      }
    } else if (action === 'activate') {
      try {
        await toggleAgencyAutomation(token, agencyId, workflowId);
        toast.success('Automation activated');
        fetchAutomations();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to activate automation');
      }
    } else if (action === 'archive') {
      try {
        await toggleAgencyAutomation(token, agencyId, workflowId, { status: 'archived' });
        toast.success('Automation archived');
        fetchAutomations();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to archive automation');
      }
    }
  };

  const handleConfirmDeleteWorkflow = async () => {
    if (!deleteWorkflowTarget) return;
    setDeletingWorkflow(true);
    try {
      await deleteAgencyAutomation(token, agencyId, deleteWorkflowTarget.id);
      toast.success('Automation deleted');
      setDeleteWorkflowTarget(null);
      fetchAutomations();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete automation');
    } finally {
      setDeletingWorkflow(false);
    }
  };

  const handleConfirmMoveWorkflow = async ({ category }) => {
    if (!moveFolderWorkflow) return;
    if (!category) {
      toast.info('Folders are not configured yet.');
      return;
    }
    toast.info('Folder move is available on the platform workflow list. Category folders are used here.');
    setMoveFolderWorkflow(null);
  };

  const filteredWorkflows = workflows;

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="space-y-6" data-testid="automations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Automations</h1>
          <p className="text-sm text-slate-500 mt-1">
            Create and manage automated workflows for your agency
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="gap-2">
            <FolderPlus size={16} />
            Create Folder
          </Button>
          <Button variant="outline" size="sm" className="gap-2">
            <Sparkles size={16} />
            Build with AI
          </Button>
          <div className="relative">
            <Button 
              onClick={() => setShowCreateMenu(!showCreateMenu)}
              className="gap-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700"
              data-testid="create-workflow-btn"
            >
              <Plus size={16} />
              Create Workflow
            </Button>
            {showCreateMenu && (
              <CreateWorkflowMenu
                onClose={() => setShowCreateMenu(false)}
                onSelect={handleCreateWorkflow}
                onImportFromFile={openFileImport}
                onImportFromUrl={openUrlImport}
              />
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          {[
            { id: 'workflows', label: 'Workflows', icon: Zap },
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'settings', label: 'Automation Settings', icon: Settings },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={cn(
                'flex items-center gap-2 px-1 py-3 text-sm font-medium border-b-2 transition-colors',
                activeTab === tab.id
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              )}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content - Workflows Tab */}
      {activeTab === 'workflows' && (
        <div className="flex gap-6">
          {/* Left Sidebar - Folders */}
          <div className="w-56 flex-shrink-0">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-100">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Categories</h3>
              </div>
              <nav className="p-2">
                {TRIGGER_FOLDERS.map((folder) => (
                  <button
                    key={folder.id}
                    onClick={() => setSelectedFolder(folder.id)}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors',
                      selectedFolder === folder.id
                        ? 'bg-teal-50 text-teal-700'
                        : 'text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <folder.icon size={16} />
                      <span>{folder.name}</span>
                    </div>
                  </button>
                ))}
              </nav>
              <div className="p-4 border-t border-slate-100">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Smart Lists</h3>
                <nav className="space-y-1">
                  {SMART_LISTS.map((list) => (
                    <button
                      key={list.id}
                      className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <list.icon size={16} />
                        <span>{list.name}</span>
                      </div>
                      <span className="text-xs text-slate-400">{list.count}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
            
            {/* Stats Card */}
            {stats && (
              <div className="mt-4 bg-white rounded-xl border border-slate-200 p-4">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Stats</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total Automations</span>
                    <span className="font-medium">{stats.total_automations}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Active</span>
                    <span className="font-medium text-emerald-600">{stats.active}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Runs (24h)</span>
                    <span className="font-medium">{stats.runs_24h}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Main List */}
          <div className="flex-1">
            {/* Search and Filters */}
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  placeholder="Search workflows..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button variant="outline" size="sm" className="gap-2" onClick={fetchAutomations}>
                <RefreshCw size={16} />
                Refresh
              </Button>
            </div>

            {/* Loading State */}
            {loading ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Loader2 className="w-8 h-8 text-teal-500 animate-spin mx-auto mb-4" />
                <p className="text-slate-500">Loading automations...</p>
              </div>
            ) : filteredWorkflows.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Zap className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No automations yet</p>
                <Button 
                  onClick={() => setShowCreateMenu(true)}
                  className="bg-teal-500 hover:bg-teal-600"
                >
                  <Plus size={16} className="mr-2" />
                  Create Your First Automation
                </Button>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                  <div className="col-span-5">Name</div>
                  <div className="col-span-2 text-center">Status</div>
                  <div className="col-span-2">Trigger</div>
                  <div className="col-span-2">Last Updated</div>
                  <div className="col-span-1"></div>
                </div>

                {/* Workflow Rows */}
                {filteredWorkflows.map((workflow) => (
                  <div
                    key={workflow.id}
                    className="grid grid-cols-12 gap-4 px-4 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors items-center"
                    data-testid={`workflow-row-${workflow.id}`}
                  >
                    <div className="col-span-5">
                      <Link
                        to={`/agency/${agencyId}/automations/builder/${workflow.id}`}
                        className="group"
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            'w-10 h-10 rounded-lg flex items-center justify-center',
                            workflow.status === 'active' ? 'bg-emerald-100' :
                            workflow.status === 'paused' ? 'bg-amber-100' : 'bg-slate-100'
                          )}>
                            <Zap size={18} className={cn(
                              workflow.status === 'active' ? 'text-emerald-600' :
                              workflow.status === 'paused' ? 'text-amber-600' : 'text-slate-500'
                            )} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 group-hover:text-teal-600 transition-colors">
                              {workflow.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {workflow.steps?.length || 0} steps
                            </p>
                          </div>
                        </div>
                      </Link>
                    </div>
                    <div className="col-span-2 text-center">
                      <StatusBadge status={workflow.status} />
                    </div>
                    <div className="col-span-2">
                      <span className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                        {workflow.trigger?.event?.replace(/\./g, ' ') || 'Not set'}
                      </span>
                    </div>
                    <div className="col-span-2">
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Clock size={14} />
                        {formatDate(workflow.updated_at)}
                      </div>
                    </div>
                    <div className="col-span-1 text-right" data-no-row-click onClick={(event) => event.stopPropagation()}>
                      <WorkflowActionsMenu
                        workflow={workflow}
                        variant="agency"
                        onOpenBuilder={() => handleWorkflowAction(workflow.id, 'edit')}
                        onSendTest={() => handleWorkflowAction(workflow.id, 'send-test')}
                        onPause={() => handleWorkflowAction(workflow.id, 'pause')}
                        onActivate={() => handleWorkflowAction(workflow.id, 'activate')}
                        onDuplicate={() => handleWorkflowAction(workflow.id, 'duplicate')}
                        onDownload={handleDownloadWorkflow}
                        downloadingWorkflowId={downloadingWorkflowId}
                        onMoveToFolder={() => setMoveFolderWorkflow(workflow)}
                        onArchive={() => handleWorkflowAction(workflow.id, 'archive')}
                        onDelete={() => setDeleteWorkflowTarget(workflow)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <BarChart3 className="mx-auto text-slate-300 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Automation Overview</h3>
          <p className="text-sm text-slate-500">
            View performance metrics and analytics for your automations
          </p>
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <FileText className="mx-auto text-slate-300 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Workflow Templates</h3>
          <p className="text-sm text-slate-500 mb-4">
            Browse and manage your saved workflow templates
          </p>
          <Button onClick={() => setShowTemplateLibrary(true)} className="gap-2">
            <FileText size={16} />
            Browse Template Library
          </Button>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
          <Settings className="mx-auto text-slate-300 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-slate-700 mb-2">Automation Settings</h3>
          <p className="text-sm text-slate-500">
            Configure default behaviors and agency-wide automation settings
          </p>
        </div>
      )}

      {/* Template Library Modal */}
      {showTemplateLibrary && (
        <WorkflowTemplateLibrary
          isOpen={showTemplateLibrary}
          onClose={() => setShowTemplateLibrary(false)}
          onSelectTemplate={handleSelectTemplate}
        />
      )}

      <DeleteWorkflowModal
        isOpen={Boolean(deleteWorkflowTarget)}
        workflowName={deleteWorkflowTarget?.name}
        onCancel={() => setDeleteWorkflowTarget(null)}
        onConfirm={handleConfirmDeleteWorkflow}
        confirming={deletingWorkflow}
      />

      <MoveWorkflowFolderModal
        isOpen={Boolean(moveFolderWorkflow)}
        onClose={() => setMoveFolderWorkflow(null)}
        workflow={moveFolderWorkflow}
        categories={TRIGGER_FOLDERS.filter((folder) => folder.id !== 'all').map((folder) => ({
          id: folder.id,
          name: folder.name,
        }))}
        folders={[]}
        onMove={handleConfirmMoveWorkflow}
      />

      <WorkflowImportDialog
        open={workflowImportOpen}
        onOpenChange={setWorkflowImportOpen}
        mode={workflowImportMode}
        scope="agency"
        agencyId={agencyId}
        token={token}
        onImported={handleWorkflowImported}
      />
    </div>
  );
};

export default AutomationsPage;
